import { differenceInDays } from 'date-fns';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';
import {
	deserializeState,
	useGetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { TWCG_1489_CONNECTOR_ONBOARDING } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

const FIRST_THIRTY_DAYS_THRESHOLD = 30;

/**
 * Hook for consumers to check if the connector onboarding experiment is enabled.
 * Use this to conditionally render a ConnectorPromo* entry point component.
 *
 * Eligibility criteria:
 * - User must be a founding admin (exclude nth users)
 * - User must be within the first 30 days of their enrolment date
 *
 * @example
 * ```tsx
 * const { isEnabled } = useIsConnectorOnboardingEnabled();
 * {isEnabled && <ConnectorPromoNavSearch />}
 * ```
 */
export function useIsConnectorOnboardingEnabled(): { isEnabled: boolean } {
	// Check if the user is the founding admin (exclude nth users) and fetch enrolment date
	const { data: onboardingStateData, loading: isOnboardingStateLoading } = useGetOnboardingState(
		[TWCG_1489_CONNECTOR_ONBOARDING.ENROLMENT_DATE],
		!fg('twcg-1489-cc-3p-connector-onboarding-fg'),
	);
	const { enrolmentDate } = deserializeState(onboardingStateData);

	if (isOnboardingStateLoading) {
		return { isEnabled: false };
	}

	// Only show to users within the first 30 days of their experiment enrolment
	const isWithinFirstThirtyDays =
		Boolean(enrolmentDate) &&
		differenceInDays(new Date(), new Date(enrolmentDate as string)) < FIRST_THIRTY_DAYS_THRESHOLD;

	if (!isWithinFirstThirtyDays) {
		return { isEnabled: false };
	}

	return { isEnabled: expVal('twcg-1489-cc-3p-connector-onboarding', 'isEnabled', false) };
}
